import { useEffect, useRef } from "preact/hooks";

/** Keep a visible listening session awake when the browser supports Screen Wake Lock. */
export function useScreenWakeLock(enabled: boolean): void {
  const sentinel = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    let active = true;

    const acquire = async (): Promise<void> => {
      if (!enabled || document.visibilityState !== "visible" || sentinel.current || !navigator.wakeLock) return;
      try {
        const lock = await navigator.wakeLock.request("screen");
        if (!active) {
          await lock.release();
          return;
        }
        sentinel.current = lock;
        lock.addEventListener("release", () => {
          if (sentinel.current === lock) sentinel.current = null;
        }, { once: true });
      } catch {
        // Unsupported/denied wake locks must not interrupt listening.
      }
    };

    const handleVisibility = (): void => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      active = false;
      document.removeEventListener("visibilitychange", handleVisibility);
      const lock = sentinel.current;
      sentinel.current = null;
      if (lock) void lock.release().catch(() => undefined);
    };
  }, [enabled]);
}
